import { useSyncExternalStore } from 'react';
import { useIsLockActive } from './processingLock';
import { canResumeManualHold, isPersonalizationHeld } from './OrdersView/statuses';
import type { Order } from './types';

/**
 * A saved item change starts the item's own run seconds later, and a Resume must not overtake it.
 * This timeout ends the wait for a run whose end never shows.
 */
export const ITEM_CHANGE_RUN_TIMEOUT_MS = 20_000;

/**
 * Per Order, the saved item changes whose runs have not ended: `saves` counts the ones whose run
 * has not started, and `running` is a run started for one of them.
 */
const waits = new Map<string, { saves: number; running: boolean; timer: ReturnType<typeof setTimeout> }>();
const listeners = new Set<() => void>();

const notify = (): void => {
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

function endWait(orderId: string): void {
  const wait = waits.get(orderId);
  if (!wait) return;
  clearTimeout(wait.timer);
  waits.delete(orderId);
  notify();
}

/** Records a saved item change. Only a held Order waits for its run; see `useIsOrderProcessing`. */
export function markItemChangeSaved(orderId: string): void {
  const wait = waits.get(orderId);
  clearTimeout(wait?.timer);
  waits.set(orderId, {
    saves: (wait?.saves ?? 0) + 1,
    running: wait?.running ?? false,
    timer: setTimeout(() => endWait(orderId), ITEM_CHANGE_RUN_TIMEOUT_MS),
  });
  notify();
}

/**
 * Records a run taking the Order's processing lock, from the live `orderProcessingStarted` event.
 * order-service sends it once per lock it grants, and `orderProcessingCompleted` once per release
 * after it, so a run that held the lock at the save sent its start before it and does not count.
 * Each later run counts for one saved change.
 */
export function markOrderProcessingStarted(orderId: string): void {
  const wait = waits.get(orderId);
  if (!wait || wait.saves === 0) return;
  wait.saves -= 1;
  wait.running = true;
}

/**
 * Records a counted run releasing the lock, from the live `orderProcessingCompleted` event. The wait
 * ends here, not at the run's start, because the Order page doesn't show a lock patched into the
 * cache while its own re-read of the Order is in flight. Two changes handled by one run leave one
 * save counted, so their wait ends on the timeout.
 */
export function markOrderProcessingCompleted(orderId: string): void {
  const wait = waits.get(orderId);
  if (!wait?.running) return;
  wait.running = false;
  if (wait.saves === 0) endWait(orderId);
}

/** The holds a Resume releases, the only states in which a saved item change makes Resume wait. */
function isResumableHold(status: string | null | undefined): boolean {
  return isPersonalizationHeld(status) || canResumeManualHold(status);
}

/**
 * Whether a held Order has a saved item change whose run has not ended. For code that runs outside
 * a component, such as a bulk Resume's last check before it submits.
 */
export function isItemChangePending(order: Pick<Order, 'id' | 'status'>): boolean {
  return isResumableHold(order.status) && waits.has(order.id);
}

/**
 * Whether the Order is being processed: its processing lock is active, or it is on a
 * Personalization Hold or Manual Hold and an item change saved on it has not had its run end.
 * sse-service replays no processing events after a reconnect, so a run that starts or ends while
 * the app moves between pages, as from the Order page back to the Orders list, leaves the wait to
 * the timeout.
 */
export function useIsOrderProcessing(order: Order | null | undefined): boolean {
  const lockActive = useIsLockActive(order?.processingLock ?? null);
  const orderId = order?.id ?? '';
  const pending = useSyncExternalStore(subscribe, () => waits.has(orderId));
  return lockActive || (isResumableHold(order?.status) && pending);
}
